import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add,
  ExpandMore,
  Delete,
  DragIndicator,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HarmCategorySelector } from './HarmCategorySelector';
import { GraniteCategorySelector } from './GraniteCategorySelector';
import { SemanticCategorySelector } from './SemanticCategorySelector';
import { FileUploader } from './FileUploader';
import { CurlWhirlModal } from './CurlWhirlModal';
import { AccessibleTooltip } from './AccessibleTooltip';
import { ANALYSIS_LABELS, ANALYSIS_TYPES } from '../constants/analysisLabels';
import CustomEvaluationTestBuilder, {
  ensureCustomTestIds,
  sanitizeCustomTestsForPersistence,
} from './CustomEvaluationTestBuilder';
import type { CustomEvalTestDefinition } from '../graphql/settings';

// Sortable Step Item Component
interface SortableStepItemProps {
  step: SequenceStep;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SortableStepItem({ step, index, isSelected, onSelect, onDelete }: SortableStepItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        p: 2,
        cursor: 'pointer',
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isDragging ? 'action.hover' : 'background.paper',
      }}
      onClick={onSelect}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <IconButton
            size="small"
            sx={{ 
              cursor: 'grab',
              color: 'text.secondary',
              mr: 1,
              '&:active': { cursor: 'grabbing' }
            }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {step.stepNumber}. {step.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {step.stepType === 'httpRequest' ? `${step.method} ${step.url}` : 
               step.stepType === 'timer' ? `Wait ${step.waitSeconds}s` : 
               'Action'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

// Enhanced Variable Types
type VariableType = 'string' | 'int' | 'float' | 'boolean' | 'json' | 'array' | 'object';

// Operators for different variable types
type ComparisonOperator = 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal';
type StringOperator = 'contains' | 'starts_with' | 'ends_with' | 'regex_match';
type ArrayOperator = 'includes' | 'length_equals' | 'length_greater' | 'length_less';
type ObjectOperator = 'has_key' | 'key_equals';

type AssignmentSourceType = 'static' | 'expression' | 'llm_prompt';

interface VariableAssignment {
  variableName: string;
  variableType: VariableType;
  sourceType: AssignmentSourceType;
  
  // For static values
  staticValue?: string;
  
  // For expression evaluation
  expression?: string;
  
  // For LLM-generated values
  llmPrompt?: string;
  llmModel?: 'gemini-pro' | 'gpt-4' | 'claude-3';
  llmTemperature?: number;
  llmMaxTokens?: number;
  
  // For response field extraction
  responseFieldPath?: string;
  
  // For arrays and objects, specify structure
  arrayItemType?: VariableType;
  objectSchema?: Record<string, VariableType>;
}

interface Condition {
  leftOperand: string; // variable name or literal
  operator: ComparisonOperator | StringOperator | ArrayOperator | ObjectOperator;
  rightOperand: string; // variable name or literal
  logicalOperator?: 'AND' | 'OR'; // for chaining conditions
  nextCondition?: Condition;
}

interface LoopConfig {
  loopType: 'for' | 'foreach' | 'while';
  // For 'for' loops
  variable?: string;
  start?: string;
  end?: string;
  increment?: string;
  // For 'foreach' loops
  iteratorVariable?: string;
  iterableVariable?: string; // array or object to iterate over
  keyVariable?: string; // for object iteration (key-value pairs)
  // For 'while' loops
  condition?: Condition;
}

interface ActionBlock {
  id: string;
  type: 'if' | 'loop' | 'variable_assignment' | 'transform' | 'log';
  
  // Conditional logic
  condition?: Condition;
  
  // Loop configuration
  loop?: LoopConfig;
  
  // Variable assignment
  variableAssignment?: VariableAssignment;
  
  // Data transformation
  transform?: {
    inputVariable: string;
    outputVariable: string;
    transformType: 'map' | 'filter' | 'reduce' | 'sort' | 'group';
    expression: string;
  };
  
  // Logging/debugging
  log?: {
    message: string;
    variables: string[];
    logLevel: 'info' | 'warn' | 'error' | 'debug';
  };
  
  // Nested action blocks - true visual programming
  children: ActionBlock[];
  
  // UI state
  collapsed?: boolean;
  position?: { x: number; y: number };
}

interface SequenceStep {
  id: string;
  stepNumber: number;
  name: string;
  stepType: 'httpRequest' | 'timer' | 'action';
  
  // HTTP Request fields
  url: string;
  method: string;
  headers: string;
  requestBody: string;
  requestBodyType: 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart';
  analyzeForBias: boolean;
  // Bias analysis configuration
  analysisText: string;
  retryOnFailure: boolean;
  maxRetries: number;
  retryDelay: number;
  skipCondition: {
    variable: string;
    operator: string;
    value: string;
  };
  
  // Timer fields
  waitSeconds: number;
  
  // Action fields - for visual programming
  actionBlocks: ActionBlock[];
  
  // Variable definitions that can be created in this step
  variableDefinitions: VariableAssignment[];
  
  // RAG Analysis Configuration
  ragAnalysisEnabled: boolean;
  ragQueryVariable: string;
  ragContextVariable: string;
  ragResponseVariable: string;
  
  // File Attachments for HTTP requests
  fileAttachments: { fileId: string; fieldName: string; variableName?: string }[];
}

interface SequenceBuilderProps {
  onSave: (sequenceData: any) => void;
  initialData?: any;
  readOnly?: boolean;
}

// Helper function to migrate old action format to new actionBlocks format
const migrateActionToActionBlocks = (step: any): SequenceStep => {
  console.log('🔄 [FRONTEND] Migrating step:', step);
  console.log('🔄 [FRONTEND] Step httpMethod from backend:', step.httpMethod);
  console.log('🔄 [FRONTEND] Step method field from backend:', step.method);
  console.log('🔄 [FRONTEND] Step actionBlocks from backend:', step.actionBlocks);
  console.log('🔄 [FRONTEND] Step actionBlocks type:', typeof step.actionBlocks);
  console.log('🔄 [FRONTEND] Step actionBlocks is array?', Array.isArray(step.actionBlocks));
  
  if (step.actionBlocks && Array.isArray(step.actionBlocks) && step.actionBlocks.length > 0) {
    return step;
  }

  let actionBlocks: ActionBlock[] = [];
  
  if (step.action) {
    const actionId = `action-${Date.now()}`;
    const actionBlock: ActionBlock = {
      id: actionId,
      type: step.action.type || 'if',
      children: []
    };

    if (step.action.condition) {
      actionBlock.condition = step.action.condition;
    }
    if (step.action.loop) {
      actionBlock.loop = step.action.loop;
    }
    if (step.action.variableAssignment) {
      actionBlock.variableAssignment = step.action.variableAssignment;
    }

    actionBlocks = [actionBlock];
  }

  const migratedStep = {
    ...step,
    method: step.httpMethod || step.method || 'GET', // Map httpMethod from backend to method for frontend
    actionBlocks,
    requestBodyType: (step.requestBodyType || 'json') as 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart', // Default to JSON if not specified
  };
  
  console.log('🔄 [FRONTEND] Migrated step result:', migratedStep);
  console.log('🔄 [FRONTEND] Final method field after migration:', migratedStep.method);
  return migratedStep;
};

// Helper functions for request body formatting
const getRequestBodyPlaceholder = (type: 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart'): string => {
  switch (type) {
    case 'json':
      return '{\n  "userId": {{userId}},\n  "name": "{{userName}}",\n  "email": "{{userEmail}}",\n  "metadata": {\n    "source": "{{requestSource}}",\n    "timestamp": "{{currentTime}}"\n  }\n}';
    case 'xml':
      return '<?xml version="1.0" encoding="UTF-8"?>\n<user>\n  <id>{{userId}}</id>\n  <name>{{userName}}</name>\n  <email>{{userEmail}}</email>\n  <metadata>\n    <source>{{requestSource}}</source>\n    <timestamp>{{currentTime}}</timestamp>\n  </metadata>\n</user>';
    case 'form':
      return 'userId={{userId}}&name={{userName}}&email={{userEmail}}&source={{requestSource}}';
    case 'text':
      return 'User ID: {{userId}}\nName: {{userName}}\nEmail: {{userEmail}}\nSource: {{requestSource}}';
    case 'binary':
      return '{{binaryData}} or file path or base64 encoded data';
    case 'multipart':
      return 'Additional form fields as JSON: {"field1": "value1", "field2": "{{variable}}"}';
    default:
      return '';
  }
};

const getRequestBodyHelperText = (type: 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart'): string => {
  switch (type) {
    case 'json':
      return 'Use {{variableName}} for variables. Numbers don\'t need quotes, strings do.';
    case 'xml':
      return 'Use {{variableName}} to insert variables into XML elements and attributes.';
    case 'form':
      return 'Use key=value pairs separated by &. Use {{variableName}} for variable values.';
    case 'text':
      return 'Plain text format. Use {{variableName}} anywhere in the text.';
    case 'binary':
      return 'Binary data, file references, or base64 encoded content. Use {{variableName}} for dynamic binary data.';
    case 'multipart':
      return 'Files are attached below. Use this field for additional form data as JSON key-value pairs.';
    default:
      return 'Use {{variableName}} to insert variables.';
  }
};

// File type for uploaded files
interface SequenceFile {
  id: string;
  originalFilename: string;
  fileType: 'context' | 'attachment' | 'both';
  mimeType: string;
  fileSize: number;
  variableName: string;
  description: string;
  hasTextContent: boolean;
  fileUrl: string;
  createdAt: string;
}

export const SequenceBuilder: React.FC<SequenceBuilderProps> = ({ onSave, initialData, readOnly = false }) => {
  const [sequenceName, setSequenceName] = useState('');
  const [sequenceDescription, setSequenceDescription] = useState('');
  const [sequenceStatus, setSequenceStatus] = useState<'draft' | 'active' | 'paused' | 'archived'>('draft');
  const [globalVariables, setGlobalVariables] = useState('{\n  "baseUrl": "https://api.example.com",\n  "apiKey": "your-api-key"\n}');
  const [ignoredHarmCategories, setIgnoredHarmCategories] = useState<string[]>([]);
  const [ignoredGraniteCategories, setIgnoredGraniteCategories] = useState<string[]>([]);
  
  // Analysis toggle options
  const [llamaGuardEnabled, setLlamaGuardEnabled] = useState(true);
  const [graniteGuardianEnabled, setGraniteGuardianEnabled] = useState(true);
  const [semanticAnalysisEnabled, setSemanticAnalysisEnabled] = useState(true);
  const [ignoredSemanticCategories, setIgnoredSemanticCategories] = useState<string[]>([]);
  const [customTests, setCustomTests] = useState<CustomEvalTestDefinition[]>([]);

  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  
  // File uploads state
  const [sequenceFiles, setSequenceFiles] = useState<SequenceFile[]>([]);
  const sequenceId = initialData?.id || null;
  
  // Scheduling state
  const [scheduledEnabled, setScheduledEnabled] = useState(false);
  const [scheduledDays, setScheduledDays] = useState<number[]>([]);
  const [scheduledStartTime, setScheduledStartTime] = useState('09:00');
  const [scheduledFrequencyMinutes, setScheduledFrequencyMinutes] = useState(60);
  const [scheduledMaxTimeoutMinutes, setScheduledMaxTimeoutMinutes] = useState(15);
  
  // Track if we've already initialized to prevent re-initialization on parent re-renders (e.g., from polling)
  const isInitializedRef = useRef(false);
  const initialDataIdRef = useRef<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex(step => step.id === active.id);
      const newIndex = steps.findIndex(step => step.id === over.id);

      const newSteps = arrayMove(steps, oldIndex, newIndex);
      
      // Update step numbers
      const updatedSteps = newSteps.map((step: SequenceStep, index: number) => ({
        ...step,
        stepNumber: index + 1
      }));

      setSteps(updatedSteps);

      // Update selected step index if needed
      if (selectedStepIndex !== null) {
        if (selectedStepIndex === oldIndex) {
          setSelectedStepIndex(newIndex);
        } else if (oldIndex < selectedStepIndex && newIndex >= selectedStepIndex) {
          setSelectedStepIndex(selectedStepIndex - 1);
        } else if (oldIndex > selectedStepIndex && newIndex <= selectedStepIndex) {
          setSelectedStepIndex(selectedStepIndex + 1);
        }
      }
    }
  };

  // Initialize with migrated data if provided - only runs once per sequence to prevent
  // form state from being reset when parent component re-renders due to data polling
  useEffect(() => {
    // Get a stable identifier for the current sequence being edited
    const currentDataId = initialData?.name ? `edit-${initialData.name}` : 'new';
    
    // Only initialize if:
    // 1. We haven't initialized yet, OR
    // 2. We're editing a different sequence than before
    if (isInitializedRef.current && initialDataIdRef.current === currentDataId) {
      console.log('⏭️ [FRONTEND] Skipping re-initialization - already initialized for this sequence');
      return;
    }
    
    if (initialData?.steps) {
      console.log('🔄 [FRONTEND] Loading initial data for editing:', initialData);
      console.log('🔄 [FRONTEND] Raw steps from backend:', initialData.steps);
      
      // Log each raw step from backend
      initialData.steps.forEach((step: any, index: number) => {
        console.log(`🔍 [FRONTEND] Raw step ${index} from backend:`, step);
        console.log(`🔍 [FRONTEND] Raw step ${index} httpMethod field:`, step.httpMethod);
        console.log(`🔍 [FRONTEND] Raw step ${index} method field:`, step.method);
      });
      
      const migratedSteps = initialData.steps.map(migrateActionToActionBlocks);
      console.log('📝 [FRONTEND] Migrated steps:', migratedSteps);
      
      // Log each step's method after migration
      migratedSteps.forEach((step: SequenceStep, index: number) => {
        if (step.stepType === 'httpRequest') {
          console.log(`📝 [FRONTEND] Step ${index} method after migration:`, step.method);
        }
      });
      
      setSteps(migratedSteps);
      setSequenceName(initialData.name || '');
      setSequenceDescription(initialData.description || '');
      setSequenceStatus(initialData.status || 'draft');
      setGlobalVariables(JSON.stringify(initialData.globalVariables || {}, null, 2));
      setIgnoredHarmCategories(initialData.ignoredHarmCategories || []);
      setIgnoredGraniteCategories(initialData.ignoredGraniteCategories || []);
      
      // Load analysis toggle options
      setLlamaGuardEnabled(initialData.llamaGuardEnabled ?? true);
      setGraniteGuardianEnabled(initialData.graniteGuardianEnabled ?? true);
      setSemanticAnalysisEnabled(initialData.semanticAnalysisEnabled ?? true);
      setIgnoredSemanticCategories(initialData.ignoredSemanticCategories || []);
      setCustomTests(ensureCustomTestIds(sanitizeCustomTestsForPersistence(initialData.customTests || [])));

      // Load scheduling data
      console.log('📅 [FRONTEND] Loading scheduling data from initialData:', {
        scheduledEnabled: initialData.scheduledEnabled,
        scheduledDays: initialData.scheduledDays,
        scheduledStartTime: initialData.scheduledStartTime,
        scheduledFrequencyMinutes: initialData.scheduledFrequencyMinutes,
        scheduledMaxTimeoutMinutes: initialData.scheduledMaxTimeoutMinutes,
      });
      setScheduledEnabled(initialData.scheduledEnabled ?? false);
      setScheduledDays(initialData.scheduledDays || []);
      setScheduledStartTime(initialData.scheduledStartTime || '09:00');
      setScheduledFrequencyMinutes(initialData.scheduledFrequencyMinutes || 60);
      setScheduledMaxTimeoutMinutes(initialData.scheduledMaxTimeoutMinutes || 15);
      
      // Load files
      setSequenceFiles(initialData.files || []);
    }
    
    // Mark as initialized for this sequence
    isInitializedRef.current = true;
    initialDataIdRef.current = currentDataId;
  }, [initialData]);

  // Auto-select the first step when steps are loaded and no step is selected
  useEffect(() => {
    if (steps.length > 0 && selectedStepIndex === null) {
      console.log('✅ Auto-selecting first step for editing');
      setSelectedStepIndex(0);
    }
  }, [steps, selectedStepIndex]);

  const createNewStep = (stepType: 'httpRequest' | 'timer' | 'action'): SequenceStep => {
    console.log(`🔍 [FRONTEND] Creating new step of type: ${stepType}`);
    const newStep: SequenceStep = {
      id: `${stepType}-${Date.now()}`,
      stepNumber: steps.length + 1,
      name: `New ${stepType === 'httpRequest' ? 'HTTP Request' : stepType === 'timer' ? 'Timer' : 'Action'}`,
      stepType,
      url: stepType === 'httpRequest' ? '{{baseUrl}}/api/endpoint' : '',
      method: 'POST', // Default to POST instead of GET
      headers: '{\n  "Content-Type": "application/json"\n}',
      requestBody: '',
      requestBodyType: 'json' as const,
      analyzeForBias: false,
      analysisText: '',
      waitSeconds: stepType === 'timer' ? 5 : 0,
      retryOnFailure: true,
      maxRetries: 3,
      retryDelay: 5,
      skipCondition: {
        variable: '',
        operator: 'equals',
        value: '',
      },
      actionBlocks: [],
      variableDefinitions: [],
      // RAG Analysis Configuration
      ragAnalysisEnabled: false,
      ragQueryVariable: '',
      ragContextVariable: '',
      ragResponseVariable: '',
      // File Attachments
      fileAttachments: [],
    };
    console.log(`🔍 [FRONTEND] Created new step:`, newStep);
    return newStep;
  };

  const addStep = (stepType: 'httpRequest' | 'timer' | 'action') => {
    console.log('➕ Adding new step of type:', stepType);
    console.log('📊 Current steps before adding:', steps);
    const newStep = createNewStep(stepType);
    console.log('🆕 Created new step:', newStep);
    const updatedSteps = [...steps, newStep];
    console.log('📝 Updated steps array:', updatedSteps);
    setSteps(updatedSteps);
    setSelectedStepIndex(steps.length); // Select the new step
    console.log('✅ Step added successfully, selected index:', steps.length);
  };

  const updateStep = (index: number, updatedStep: SequenceStep) => {
    console.log('🔄 [FRONTEND] updateStep called for index:', index);
    console.log('🔄 [FRONTEND] Current step before update:', steps[index]);
    console.log('🔄 [FRONTEND] Updated step data:', updatedStep);
    console.log('🔄 [FRONTEND] Method field in updated step:', updatedStep.method);
    
    const newSteps = [...steps];
    newSteps[index] = { ...updatedStep, stepNumber: index + 1 };
    setSteps(newSteps);
    
    console.log('✅ [FRONTEND] Steps updated successfully');
    console.log('✅ [FRONTEND] Updated step at index', index, ':', newSteps[index]);
    console.log('✅ [FRONTEND] Method field after update:', newSteps[index].method);
    console.log('✅ [FRONTEND] All steps after update:', newSteps);
  };

  const deleteStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    // Update step numbers
    const updatedSteps = newSteps.map((step, i) => ({ ...step, stepNumber: i + 1 }));
    setSteps(updatedSteps);
    
    // Clear selection if deleted step was selected
    if (selectedStepIndex === index) {
    setSelectedStepIndex(null);
    } else if (selectedStepIndex !== null && selectedStepIndex > index) {
      setSelectedStepIndex(selectedStepIndex - 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sequenceData = {
        name: sequenceName,
        description: sequenceDescription,
        status: sequenceStatus,
        globalVariables: JSON.parse(globalVariables),
        ignoredHarmCategories: ignoredHarmCategories,
        ignoredGraniteCategories: ignoredGraniteCategories,
        llama_guard_enabled: llamaGuardEnabled,
        granite_guardian_enabled: graniteGuardianEnabled,
        semantic_analysis_enabled: semanticAnalysisEnabled,
        ignored_semantic_categories: ignoredSemanticCategories,
        customTests: sanitizeCustomTestsForPersistence(ensureCustomTestIds(customTests)),
        scheduledEnabled: scheduledEnabled,
        scheduledDays: scheduledDays,
        scheduledStartTime: scheduledStartTime,
        scheduledFrequencyMinutes: Math.max(5, scheduledFrequencyMinutes), // Minimum 5 minutes
        scheduledMaxTimeoutMinutes: scheduledMaxTimeoutMinutes,
        steps: steps,
      };
      console.log('💾 [FRONTEND] Saving sequence with scheduling data:', {
        scheduledEnabled: sequenceData.scheduledEnabled,
        scheduledDays: sequenceData.scheduledDays,
        scheduledStartTime: sequenceData.scheduledStartTime,
        scheduledFrequencyMinutes: sequenceData.scheduledFrequencyMinutes,
        scheduledMaxTimeoutMinutes: sequenceData.scheduledMaxTimeoutMinutes,
      });
      await onSave(sequenceData);
    } catch (error) {
      console.error('Error saving sequence:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component={readOnly ? 'fieldset' : 'div'}
      disabled={readOnly || undefined}
      sx={{
        height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
        border: 'none', p: 0, m: 0,
        ...(readOnly && { opacity: 0.85 }),
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {/* Save Button Row */}
        <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            data-ga-label="SequenceBuilder Save"
            variant="contained"
            onClick={handleSave}
            disabled={readOnly || saving || !sequenceName.trim()}
            sx={{ 
              height: '48px', 
              px: 4,
              bgcolor: '#1e3a5f',
              '&:hover': {
                bgcolor: '#059669'
              },
              '&:disabled': {
                bgcolor: '#9ca3af'
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Sequence'}
          </Button>
        </Box>
        
        {/* Sequence Details Row */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Sequence Name"
              value={sequenceName}
              onChange={(e) => setSequenceName(e.target.value)}
              placeholder="My API Test Sequence"
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Description"
              value={sequenceDescription}
              onChange={(e) => setSequenceDescription(e.target.value)}
              placeholder="Description of what this sequence does"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={sequenceStatus}
                label="Status"
                onChange={(e) => setSequenceStatus(e.target.value as any)}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Global Variables */}
      <Box sx={{ mb: 3 }}>
        <Accordion sx={{ overflow: 'visible' }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography variant="h6">Global Variables</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Global Variables (JSON)"
              value={globalVariables}
              onChange={(e) => setGlobalVariables(e.target.value)}
              placeholder='{\n  "baseUrl": "https://api.example.com",\n  "apiKey": "your-api-key"\n}'
              sx={{ fontFamily: 'monospace' }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Analysis Engines - Combined Toggle + Settings */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* General Safety & Policy Monitoring */}
        <Accordion sx={{ overflow: 'visible', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />} 
            sx={{ overflow: 'visible' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Switch
                checked={llamaGuardEnabled}
                onChange={(e) => {
                  e.stopPropagation();
                  setLlamaGuardEnabled(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                color="primary"
              />
              <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].tooltip}>
                <Box tabIndex={0} sx={{ cursor: 'help', flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Monitors for unsafe content, policy violations, and high-risk interaction patterns.
                  </Typography>
                </Box>
              </AccessibleTooltip>
              {ignoredHarmCategories.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({ignoredHarmCategories.length} ignored)
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <HarmCategorySelector
              value={ignoredHarmCategories}
              onChange={setIgnoredHarmCategories}
            />
          </AccordionDetails>
        </Accordion>

        {/* Standards & Regulatory Compliance Analysis */}
        <Accordion sx={{ overflow: 'visible', bgcolor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />} 
            sx={{ overflow: 'visible' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Switch
                checked={graniteGuardianEnabled}
                onChange={(e) => {
                  e.stopPropagation();
                  setGraniteGuardianEnabled(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                color="primary"
              />
              <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].tooltip}>
                <Box tabIndex={0} sx={{ cursor: 'help', flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Evaluates alignment with ISO/IEC, EU AI Act, and NIST AI RMF frameworks.
                  </Typography>
                </Box>
              </AccessibleTooltip>
              {ignoredGraniteCategories.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({ignoredGraniteCategories.length} ignored)
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <GraniteCategorySelector
              value={ignoredGraniteCategories}
              onChange={setIgnoredGraniteCategories}
            />
          </AccordionDetails>
        </Accordion>

        {/* User Perception & Experience Analysis */}
        <Accordion sx={{ overflow: 'visible', bgcolor: '#fdf4ff', border: '1px solid #bae6fd', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />} 
            sx={{ overflow: 'visible' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Switch
                checked={semanticAnalysisEnabled}
                onChange={(e) => {
                  e.stopPropagation();
                  setSemanticAnalysisEnabled(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                color="primary"
              />
              <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].tooltip}>
                <Box tabIndex={0} sx={{ cursor: 'help', flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Evaluates tone, clarity, trust impact, and likelihood of confusion or misuse.
                  </Typography>
                </Box>
              </AccessibleTooltip>
              {ignoredSemanticCategories.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({ignoredSemanticCategories.length} ignored)
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <SemanticCategorySelector
              value={ignoredSemanticCategories}
              onChange={setIgnoredSemanticCategories}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Custom evaluation tests (same schema as Evaluation API custom tests) */}
      <Box sx={{ mb: 3 }}>
        <Accordion sx={{ overflow: 'visible', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography variant="h6">
              Custom tests
              {customTests.length > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  ({customTests.length} configured)
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <CustomEvaluationTestBuilder
              variant="sequence"
              generateFromPromptContext="sequence"
              tests={customTests}
              onChange={setCustomTests}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* File Uploads - Only shown when editing an existing sequence */}
      {sequenceId && (
        <Box sx={{ mb: 3 }}>
          <Accordion sx={{ overflow: 'visible' }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
              <Typography variant="h6">
                📁 File Uploads
                {sequenceFiles.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    ({sequenceFiles.length} files)
                  </Typography>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FileUploader
                sequenceId={sequenceId}
                files={sequenceFiles}
                onFileUploaded={(file) => {
                  setSequenceFiles([...sequenceFiles, file]);
                }}
                onFileDeleted={(fileId) => {
                  setSequenceFiles(sequenceFiles.filter(f => f.id !== fileId));
                }}
                title="Sequence Files"
                description="Upload files for RAG context, API attachments, or storing responses. Set a variable name to make file content available in your steps."
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      
      {/* Info message for new sequences */}
      {!sequenceId && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#e0f2fe', borderRadius: 2, border: '1px solid #7dd3fc' }}>
          <Typography variant="body2" sx={{ color: '#0e7490' }}>
            📁 <strong>File Uploads:</strong> Save this sequence first, then you can upload files for RAG context and API attachments.
          </Typography>
        </Box>
      )}

      {/* Scheduling Settings */}
      <Box sx={{ mb: 3 }}>
        <Accordion sx={{ overflow: 'visible' }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography variant="h6">
              Scheduling
              {scheduledEnabled && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  (Enabled)
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduledEnabled}
                    onChange={(e) => setScheduledEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#1e3a5f',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1e3a5f',
                      },
                    }}
                  />
                }
                label="Enable Scheduled Execution"
              />
              
              {scheduledEnabled && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Select Days to Run</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                      <Chip
                        key={day}
                        label={day}
                        onClick={() => {
                          if (scheduledDays.includes(index)) {
                            setScheduledDays(scheduledDays.filter(d => d !== index));
                          } else {
                            setScheduledDays([...scheduledDays, index].sort());
                          }
                        }}
                        color={scheduledDays.includes(index) ? 'primary' : 'default'}
                        variant={scheduledDays.includes(index) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                  
                  <TextField
                    label="Start Time"
                    type="time"
                    value={scheduledStartTime}
                    onChange={(e) => setScheduledStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Time of day to start running the sequence"
                    fullWidth
                  />
                  
                  <TextField
                    label="Frequency (minutes)"
                    type="number"
                    value={scheduledFrequencyMinutes}
                    onChange={(e) => setScheduledFrequencyMinutes(Math.max(5, parseInt(e.target.value) || 5))}
                    helperText="How often to run (minimum 5 minutes)"
                    inputProps={{ min: 5 }}
                    fullWidth
                  />
                  
                  <TextField
                    label="Max Timeout (minutes)"
                    type="number"
                    value={scheduledMaxTimeoutMinutes}
                    onChange={(e) => setScheduledMaxTimeoutMinutes(Math.max(1, parseInt(e.target.value) || 15))}
                    helperText="Maximum execution time before timeout (default: 15 minutes)"
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
          
      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, gap: 2 }}>
        {/* Left Panel - Steps List */}
        <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Steps ({steps.length})</Typography>
            <Box>
            <Button
              data-ga-label="SequenceBuilder Add HTTP"
              size="small"
              variant="outlined"
              onClick={() => addStep('httpRequest')}
                sx={{ mr: 1 }}
            >
                + HTTP
            </Button>
            <Button
              data-ga-label="SequenceBuilder Add Timer"
              size="small"
              variant="outlined"
              onClick={() => addStep('timer')}
                sx={{ mr: 1 }}
            >
                + Timer
            </Button>
            <Button
              data-ga-label="SequenceBuilder Add Action"
              size="small"
              variant="outlined"
              onClick={() => addStep('action')}
            >
                + Action
            </Button>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {steps.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography>No steps added yet</Typography>
                <Typography variant="body2">Click the buttons above to add your first step</Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map(step => step.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {steps.map((step, index) => (
                    <SortableStepItem
                      key={step.id}
                      step={step}
                      index={index}
                      isSelected={selectedStepIndex === index}
                      onSelect={() => setSelectedStepIndex(index)}
                      onDelete={() => deleteStep(index)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </Box>

        {/* Right Panel - Step Configuration */}
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
          {selectedStepIndex !== null && selectedStepIndex < steps.length ? (
            <StepConfigurationPanel
              key={steps[selectedStepIndex].id}
              step={steps[selectedStepIndex]}
              allSteps={steps}
              onUpdate={(updatedStep) => updateStep(selectedStepIndex, updatedStep)}
              onDelete={() => deleteStep(selectedStepIndex)}
              sequenceFiles={sequenceFiles}
              sequenceId={sequenceId}
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: 'text.secondary'
            }}>
              <Typography>Select a step to configure</Typography>
            </Box>
          )}
      </Box>
      </Box>
    </Box>
  );
};

// Advanced Visual Programming Action Blocks Component
const ActionBlocksSection: React.FC<{
  actionBlocks: ActionBlock[];
  onUpdate: (actionBlocks: ActionBlock[]) => void;
}> = ({ actionBlocks, onUpdate }) => {
  
  const addActionBlock = (type: ActionBlock['type'], parentId?: string) => {
    const newBlock: ActionBlock = {
      id: `block-${Date.now()}`,
      type,
      children: [],
      collapsed: false,
    };

    // Initialize based on type
  switch (type) {
      case 'if':
        newBlock.condition = {
          leftOperand: '',
          operator: 'equals',
          rightOperand: ''
        };
        break;
      case 'loop':
        newBlock.loop = {
          loopType: 'for',
          variable: 'i',
          start: '0',
          end: '10',
          increment: '1'
        };
        break;
      case 'variable_assignment':
        newBlock.variableAssignment = {
          variableName: '',
          variableType: 'string',
          sourceType: 'static',
          expression: ''
        };
        break;
      case 'transform':
        newBlock.transform = {
          inputVariable: '',
          outputVariable: '',
          transformType: 'map',
          expression: ''
        };
        break;
      case 'log':
        newBlock.log = {
          message: '',
          variables: [],
          logLevel: 'info'
        };
        break;
    }

    if (parentId) {
      // Add to specific parent
      const updateBlocks = (blocks: ActionBlock[]): ActionBlock[] => {
        return blocks.map(block => {
          if (block.id === parentId) {
            return { ...block, children: [...block.children, newBlock] };
          }
          if (block.children.length > 0) {
            return { ...block, children: updateBlocks(block.children) };
          }
          return block;
        });
      };
      onUpdate(updateBlocks(actionBlocks));
    } else {
      // Add to root level
      onUpdate([...actionBlocks, newBlock]);
    }
  };

  const updateActionBlock = (blockId: string, updates: Partial<ActionBlock>) => {
    const updateBlock = (blocks: ActionBlock[]): ActionBlock[] => {
      return blocks.map(block => {
        if (block.id === blockId) {
          return { ...block, ...updates };
        }
        if (block.children.length > 0) {
          return { ...block, children: updateBlock(block.children) };
        }
        return block;
      });
    };
    onUpdate(updateBlock(actionBlocks));
  };

  const deleteActionBlock = (blockId: string) => {
    const filterBlocks = (blocks: ActionBlock[]): ActionBlock[] => {
      return blocks.filter(block => block.id !== blockId).map(block => ({
        ...block,
        children: filterBlocks(block.children)
      }));
    };
    onUpdate(filterBlocks(actionBlocks));
  };

  const moveBlock = (dragIndex: number, hoverIndex: number, parentBlocks: ActionBlock[]) => {
    const dragBlock = parentBlocks[dragIndex];
    const newBlocks = [...parentBlocks];
    newBlocks.splice(dragIndex, 1);
    newBlocks.splice(hoverIndex, 0, dragBlock);
    onUpdate(newBlocks);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#1e293b' }}>Visual Programming</Typography>
        <Box>
          <Button size="small" onClick={() => addActionBlock('if')} sx={{ mr: 1, bgcolor: '#dbeafe', color: '#1e40af', '&:hover': { bgcolor: '#bfdbfe' } }}>
            + IF
          </Button>
          <Button size="small" onClick={() => addActionBlock('loop')} sx={{ mr: 1, bgcolor: '#e0f2fe', color: '#0e7490', '&:hover': { bgcolor: '#ddd6fe' } }}>
            + Loop
          </Button>
          <Button size="small" onClick={() => addActionBlock('variable_assignment')} sx={{ mr: 1, bgcolor: '#e0f2fe', color: '#0e7490', '&:hover': { bgcolor: '#bae6fd' } }}>
            + Variable
          </Button>
          <Button size="small" onClick={() => addActionBlock('transform')} sx={{ mr: 1, bgcolor: '#e0e7ff', color: '#3730a3', '&:hover': { bgcolor: '#c7d2fe' } }}>
            + Transform
          </Button>
          <Button size="small" onClick={() => addActionBlock('log')} sx={{ bgcolor: '#f1f5f9', color: '#475569', '&:hover': { bgcolor: '#e2e8f0' } }}>
            + Log
          </Button>
        </Box>
      </Box>

      <Box sx={{ minHeight: '200px', border: '2px dashed #7dd3fc', borderRadius: 2, p: 2, bgcolor: '#faf5ff' }}>
        {actionBlocks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>No action blocks yet</Typography>
            <Typography variant="body2">Add blocks above to start visual programming</Typography>
          </Box>
        ) : (
          (() => {
            console.log('🔍 [FRONTEND] ActionBlocksSection passing blocks to renderer:', actionBlocks);
            console.log('🔍 [FRONTEND] ActionBlocks type:', typeof actionBlocks);
            console.log('🔍 [FRONTEND] ActionBlocks is array?', Array.isArray(actionBlocks));
            return (
              <ActionBlockRenderer
                blocks={actionBlocks}
                onUpdate={updateActionBlock}
                onDelete={deleteActionBlock}
                onAddChild={addActionBlock}
                depth={0}
              />
            );
          })()
        )}
      </Box>
    </Box>
  );
};

// Recursive component for rendering nested action blocks
const ActionBlockRenderer: React.FC<{
  blocks: ActionBlock[];
  onUpdate: (blockId: string, updates: Partial<ActionBlock>) => void;
  onDelete: (blockId: string) => void;
  onAddChild: (type: ActionBlock['type'], parentId: string) => void;
  depth: number;
}> = ({ blocks, onUpdate, onDelete, onAddChild, depth }) => {
  
  // Debug logging and defensive programming
  console.log('🔍 [FRONTEND] ActionBlockRenderer received blocks:', blocks);
  console.log('🔍 [FRONTEND] Blocks type:', typeof blocks);
  console.log('🔍 [FRONTEND] Is blocks array?', Array.isArray(blocks));
  console.log('🔍 [FRONTEND] Blocks keys:', blocks && typeof blocks === 'object' ? Object.keys(blocks) : 'N/A');
  
  // Ensure blocks is always an array
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  console.log('🔍 [FRONTEND] Safe blocks:', safeBlocks);
  
  const getBlockColor = (type: ActionBlock['type']) => {
      switch (type) {
      case 'if': return '#dbeafe';        // Blue-100 - conditionals
      case 'loop': return '#e0f2fe';      // Violet-100 - loops
      case 'variable_assignment': return '#e0f2fe'; // Fuchsia-100 - variables
      case 'transform': return '#e0e7ff'; // Indigo-100 - transforms
      case 'log': return '#f1f5f9';       // Slate-100 - logs
      default: return '#f8fafc';
    }
  };

  const getBlockIcon = (type: ActionBlock['type']) => {
      switch (type) {
      case 'if': return '◆';
      case 'loop': return '↻';
      case 'variable_assignment': return '≡';
      case 'transform': return '⟳';
      case 'log': return '▤';
      default: return '●';
      }
    };

  const getBlockBorderColor = (type: ActionBlock['type']) => {
      switch (type) {
      case 'if': return '#93c5fd';        // Blue-300
      case 'loop': return '#7dd3fc';      // Violet-300
      case 'variable_assignment': return '#67e8f9'; // Fuchsia-300
      case 'transform': return '#a5b4fc'; // Indigo-300
      case 'log': return '#cbd5e1';       // Slate-300
      default: return '#e2e8f0';
    }
  };

  const getBlockTextColor = (type: ActionBlock['type']) => {
      switch (type) {
      case 'if': return '#1e40af';        // Blue-800
      case 'loop': return '#0e7490';      // Violet-800
      case 'variable_assignment': return '#0e7490'; // Fuchsia-800
      case 'transform': return '#3730a3'; // Indigo-800
      case 'log': return '#475569';       // Slate-600
      default: return '#334155';
    }
  };

    return (
    <Box>
      {safeBlocks.map((block, index) => (
        <Paper
          key={block.id}
          sx={{
            mb: 1,
            ml: depth * 2,
            backgroundColor: getBlockColor(block.type),
            border: `2px solid ${getBlockBorderColor(block.type)}`,
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Block Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 1.5, fontSize: '1.1rem', color: getBlockTextColor(block.type) }}>{getBlockIcon(block.type)}</Typography>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: getBlockTextColor(block.type), letterSpacing: '0.5px' }}>
                  {block.type.replace('_', ' ').toUpperCase()}
                </Typography>
              </Box>
              <Box>
                <Button
                  size="small"
                  onClick={() => onUpdate(block.id, { collapsed: !block.collapsed })}
                  sx={{ mr: 1, minWidth: 32, color: getBlockTextColor(block.type) }}
                >
                  {block.collapsed ? '▸' : '▾'}
                </Button>
                <Button size="small" sx={{ minWidth: 32, color: '#be123c', '&:hover': { bgcolor: 'rgba(190, 18, 60, 0.1)' } }} onClick={() => onDelete(block.id)}>
                  ✕
                </Button>
              </Box>
          </Box>

            {/* Block Configuration */}
            {!block.collapsed && (
              <Box>
                <ActionBlockConfiguration
                  block={block}
                  onUpdate={(updates) => onUpdate(block.id, updates)}
                />

                {/* Child Blocks */}
                {(block.type === 'if' || block.type === 'loop') && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {block.type === 'if' ? 'Then:' : 'Loop Body:'}
                      </Typography>
                      <Box>
                        <Button size="small" onClick={() => onAddChild('if', block.id)} sx={{ mr: 0.5, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#1e40af', '&:hover': { bgcolor: '#bfdbfe' } }}>
                          + IF
                        </Button>
                        <Button size="small" onClick={() => onAddChild('loop', block.id)} sx={{ mr: 0.5, fontSize: '0.7rem', bgcolor: '#e0f2fe', color: '#0e7490', '&:hover': { bgcolor: '#ddd6fe' } }}>
                          + Loop
                        </Button>
                        <Button size="small" onClick={() => onAddChild('variable_assignment', block.id)} sx={{ mr: 0.5, fontSize: '0.7rem', bgcolor: '#e0f2fe', color: '#0e7490', '&:hover': { bgcolor: '#bae6fd' } }}>
                          + Var
                        </Button>
                        <Button size="small" onClick={() => onAddChild('transform', block.id)} sx={{ mr: 0.5, fontSize: '0.7rem', bgcolor: '#e0e7ff', color: '#3730a3', '&:hover': { bgcolor: '#c7d2fe' } }}>
                          + Transform
                        </Button>
                      </Box>
                    </Box>
                    
                    <Box sx={{ border: '2px dashed #a5b4fc', borderRadius: 2, p: 1.5, minHeight: '50px', bgcolor: 'rgba(255,255,255,0.5)' }}>
                      {block.children.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                          Add blocks here
                        </Typography>
                      ) : (
                        <ActionBlockRenderer
                          blocks={block.children}
                          onUpdate={onUpdate}
                          onDelete={onDelete}
                          onAddChild={onAddChild}
                          depth={depth + 1}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// Configuration component for individual action blocks
const ActionBlockConfiguration: React.FC<{
  block: ActionBlock;
  onUpdate: (updates: Partial<ActionBlock>) => void;
}> = ({ block, onUpdate }) => {
  
  const renderConditionConfig = (condition: Condition) => (
    <Grid container spacing={2}>
      <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
          label="Left Operand"
          value={condition.leftOperand}
          onChange={(e) => onUpdate({
            condition: { ...condition, leftOperand: e.target.value }
          })}
          placeholder="variable or value"
                />
              </Grid>
      <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Operator</InputLabel>
                  <Select
            value={condition.operator}
            onChange={(e) => onUpdate({
              condition: { ...condition, operator: e.target.value as any }
            })}
                  >
                    <MenuItem value="equals">Equals</MenuItem>
                    <MenuItem value="not_equals">Not Equals</MenuItem>
                    <MenuItem value="greater_than">Greater Than</MenuItem>
                    <MenuItem value="less_than">Less Than</MenuItem>
            <MenuItem value="contains">Contains</MenuItem>
            <MenuItem value="starts_with">Starts With</MenuItem>
            <MenuItem value="ends_with">Ends With</MenuItem>
            <MenuItem value="includes">Includes (Array)</MenuItem>
            <MenuItem value="has_key">Has Key (Object)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
      <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
          label="Right Operand"
          value={condition.rightOperand}
          onChange={(e) => onUpdate({
            condition: { ...condition, rightOperand: e.target.value }
          })}
          placeholder="variable or value"
                />
              </Grid>
            </Grid>
  );

  const renderLoopConfig = (loop: LoopConfig) => (
    <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Loop Type</InputLabel>
            <Select
              value={loop.loopType}
              onChange={(e) => onUpdate({
                loop: { ...loop, loopType: e.target.value as any }
              })}
            >
              <MenuItem value="for">For Loop (numeric)</MenuItem>
              <MenuItem value="foreach">For Each (arrays/objects)</MenuItem>
              <MenuItem value="while">While Loop</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loop.loopType === 'for' && (
        <Grid container spacing={2}>
          <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
              label="Variable"
              value={loop.variable || 'i'}
              onChange={(e) => onUpdate({
                loop: { ...loop, variable: e.target.value }
              })}
                />
              </Grid>
          <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start"
              value={loop.start || '0'}
              onChange={(e) => onUpdate({
                loop: { ...loop, start: e.target.value }
              })}
                />
              </Grid>
          <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End"
              value={loop.end || '10'}
              onChange={(e) => onUpdate({
                loop: { ...loop, end: e.target.value }
              })}
                />
              </Grid>
          <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Increment"
              value={loop.increment || '1'}
              onChange={(e) => onUpdate({
                loop: { ...loop, increment: e.target.value }
              })}
                />
              </Grid>
            </Grid>
          )}

      {loop.loopType === 'foreach' && (
              <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Iterator Variable"
              value={loop.iteratorVariable || 'item'}
              onChange={(e) => onUpdate({
                loop: { ...loop, iteratorVariable: e.target.value }
              })}
              placeholder="item"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Iterable Variable"
              value={loop.iterableVariable || ''}
              onChange={(e) => onUpdate({
                loop: { ...loop, iterableVariable: e.target.value }
              })}
              placeholder="myArray"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Key Variable (for objects)"
              value={loop.keyVariable || ''}
              onChange={(e) => onUpdate({
                loop: { ...loop, keyVariable: e.target.value }
              })}
              placeholder="key (optional)"
            />
          </Grid>
        </Grid>
      )}

      {loop.loopType === 'while' && loop.condition && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>While Condition:</Typography>
          {renderConditionConfig(loop.condition)}
        </Box>
      )}
    </Box>
  );

  const renderVariableAssignmentConfig = (varAssign: VariableAssignment) => (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="Variable Name"
          value={varAssign.variableName}
          onChange={(e) => onUpdate({
            variableAssignment: { ...varAssign, variableName: e.target.value }
          })}
          placeholder="myVariable"
        />
      </Grid>
      
      <Grid item xs={6} sm={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Type</InputLabel>
          <Select
            value={varAssign.variableType}
            onChange={(e) => onUpdate({
              variableAssignment: { ...varAssign, variableType: e.target.value as VariableType }
            })}
          >
            <MenuItem value="string">String</MenuItem>
            <MenuItem value="int">Integer</MenuItem>
            <MenuItem value="float">Float</MenuItem>
            <MenuItem value="boolean">Boolean</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
            <MenuItem value="array">Array</MenuItem>
            <MenuItem value="object">Object</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Source</InputLabel>
          <Select
            value={varAssign.sourceType || 'static'}
            onChange={(e) => onUpdate({
              variableAssignment: { ...varAssign, sourceType: e.target.value as AssignmentSourceType }
            })}
          >
            <MenuItem value="static">Static Value</MenuItem>
            <MenuItem value="expression">Expression</MenuItem>
            <MenuItem value="llm_prompt">🤖 LLM Prompt</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Dynamic input based on source type */}
      {(varAssign.sourceType === 'static' || !varAssign.sourceType) && (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Static Value"
              value={varAssign.staticValue || varAssign.expression || ''}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, staticValue: e.target.value, expression: e.target.value }
              })}
              placeholder={varAssign.variableType === 'string' ? '"Hello World"' : 
                          varAssign.variableType === 'int' ? '42' :
                          varAssign.variableType === 'float' ? '3.14' :
                          varAssign.variableType === 'boolean' ? 'true' :
                          varAssign.variableType === 'json' ? '{"key": "value"}' : 'value'}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              💡 Example: {varAssign.variableType === 'string' ? 'Enter "admin" for a username or "test@example.com" for an email' : 
                         varAssign.variableType === 'int' ? 'Enter 100 for a user ID or 25 for an age' :
                         varAssign.variableType === 'float' ? 'Enter 99.99 for a price or 4.5 for a rating' :
                         varAssign.variableType === 'boolean' ? 'Enter true for enabled status or false for disabled' :
                         varAssign.variableType === 'json' ? 'Enter {"name": "John", "role": "admin"} for user data' : 
                         varAssign.variableType === 'array' ? 'Enter ["item1", "item2", "item3"] for a list' :
                         'Enter a fixed value that never changes during execution'}
            </Typography>
          </Grid>
        </>
      )}

      {varAssign.sourceType === 'expression' && (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Expression"
              value={varAssign.expression || ''}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, expression: e.target.value }
              })}
              placeholder="{{responseData.userId}} or Math.random()"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              💡 Examples: <code>{'{{response.data.id}}'}</code> (extract from API response), <code>{'{{userName}}_{{timestamp}}'}</code> (combine variables), <code>Math.random() * 100</code> (JavaScript expression), <code>{'{{items}}.length'}</code> (array length)
            </Typography>
          </Grid>
        </>
      )}


      {varAssign.sourceType === 'llm_prompt' && (
        <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              label="🤖 LLM Prompt"
              value={varAssign.llmPrompt || ''}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, llmPrompt: e.target.value }
              })}
              placeholder={`Generate a ${varAssign.variableType} value. Examples:
• "Generate a random nationality"
• "Create a realistic email for {{userName}}"
• "Generate test data: age between 18-65"
• "Create a JSON object with user preferences"`}
              helperText="AI will generate values based on your prompt. Use {{variables}} for context."
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>AI Model</InputLabel>
              <Select
                value={varAssign.llmModel || 'gemini-pro'}
                onChange={(e) => onUpdate({
                  variableAssignment: { ...varAssign, llmModel: e.target.value as any }
                })}
              >
                <MenuItem value="gemini-pro">Gemini Pro</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="claude-3">Claude 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Temperature"
              value={varAssign.llmTemperature || 0.7}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, llmTemperature: parseFloat(e.target.value) }
              })}
              inputProps={{ min: 0, max: 2, step: 0.1 }}
              helperText="0=deterministic, 1=creative"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Tokens"
              value={varAssign.llmMaxTokens || 100}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, llmMaxTokens: parseInt(e.target.value) }
              })}
              inputProps={{ min: 1, max: 4000 }}
            />
          </Grid>
        </>
      )}

      {/* Additional fields for complex types */}
      {varAssign.variableType === 'array' && (
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Array Item Type</InputLabel>
            <Select
              value={varAssign.arrayItemType || 'string'}
              onChange={(e) => onUpdate({
                variableAssignment: { ...varAssign, arrayItemType: e.target.value as VariableType }
              })}
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="int">Integer</MenuItem>
              <MenuItem value="float">Float</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
              <MenuItem value="object">Object</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box>
      {block.type === 'if' && block.condition && renderConditionConfig(block.condition)}
      
      {block.type === 'loop' && block.loop && renderLoopConfig(block.loop)}
      
      {block.type === 'variable_assignment' && block.variableAssignment && 
        renderVariableAssignmentConfig(block.variableAssignment)}
      
      
      {block.type === 'transform' && block.transform && (
        <Grid container spacing={2}>
          <Grid item xs={3}>
                          <TextField
                            fullWidth
                            size="small"
              label="Input Variable"
              value={block.transform.inputVariable}
              onChange={(e) => onUpdate({
                transform: { ...block.transform!, inputVariable: e.target.value }
              })}
                          />
                        </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Transform</InputLabel>
              <Select
                value={block.transform.transformType}
                onChange={(e) => onUpdate({
                  transform: { ...block.transform!, transformType: e.target.value as any }
                })}
              >
                <MenuItem value="map">Map</MenuItem>
                <MenuItem value="filter">Filter</MenuItem>
                <MenuItem value="reduce">Reduce</MenuItem>
                <MenuItem value="sort">Sort</MenuItem>
                <MenuItem value="group">Group</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
                          <TextField
                            fullWidth
                            size="small"
              label="Output Variable"
              value={block.transform.outputVariable}
              onChange={(e) => onUpdate({
                transform: { ...block.transform!, outputVariable: e.target.value }
              })}
                          />
                        </Grid>
          <Grid item xs={3}>
            <TextField
                            fullWidth
                            size="small"
              label="Expression"
              value={block.transform.expression}
              onChange={(e) => onUpdate({
                transform: { ...block.transform!, expression: e.target.value }
              })}
            />
                        </Grid>
                      </Grid>
      )}
      
      {block.type === 'log' && block.log && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
                            size="small"
              label="Message"
              value={block.log.message}
              onChange={(e) => onUpdate({
                log: { ...block.log!, message: e.target.value }
              })}
            />
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select
                value={block.log.logLevel}
                onChange={(e) => onUpdate({
                  log: { ...block.log!, logLevel: e.target.value as any }
                })}
              >
                <MenuItem value="debug">Debug</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warn">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
                            size="small"
              label="Variables (comma-separated)"
              value={block.log.variables?.join(', ') || ''}
              onChange={(e) => onUpdate({
                log: { ...block.log!, variables: e.target.value.split(',').map(v => v.trim()) }
              })}
            />
          </Grid>
        </Grid>
      )}
                  </Box>
                );
              };
              
              interface StepConfigurationPanelProps {
                step: SequenceStep;
                allSteps: SequenceStep[];
                onUpdate: (step: SequenceStep) => void;
                onDelete: () => void;
                sequenceFiles?: SequenceFile[];
                sequenceId?: string | null;  // For loading RAG context files in Curl Whirl
              }
              
              const StepConfigurationPanel: React.FC<StepConfigurationPanelProps> = ({ step, allSteps, onUpdate, onDelete, sequenceFiles = [], sequenceId }) => {
  console.log('🎯 StepConfigurationPanel rendered with step:', step);
  console.log('📊 Step type detected:', step.stepType);
  
  // State for Curl Whirl modal
  const [curlWhirlOpen, setCurlWhirlOpen] = React.useState(false);
  
  // Handler for applying parsed curl result with smart suggestions
  const handleCurlWhirlApply = (result: {
    url: string;
    method: string;
    headers: Record<string, string>;
    headersJson: string;
    body: string;
    bodyType: 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart';
  }, suggestions?: {
    step_name?: string;
    base_url?: string;
    extracted_variables?: Array<{ name: string; path: string; value?: string; description?: string }>;
    variable_substitutions?: Array<{
      location: string;
      field: string;
      current_value: string;
      suggested_variable: string;
    }>;
    bias_analysis?: { enabled: boolean; text_path?: string; text_preview?: string };
    rag_analysis?: { 
      enabled: boolean; 
      query_path?: string; 
      context_path?: string; 
      response_path?: string;
      query_preview?: string;
      context_preview?: string;
      response_preview?: string;
    };
    skip_condition?: {
      variable: string;
      operator: string;
      value: string;
      description?: string;
    };
  }) => {
    console.log('🌀 [CURL WHIRL] Applying parsed result:', result);
    console.log('🌀 [CURL WHIRL] Smart suggestions:', suggestions);
    
    // Build the updated step with curl data
    let headersToUse = result.headersJson;
    
    // Apply variable substitutions to headers (e.g., replace Bearer token with {{accessToken}})
    if (suggestions?.variable_substitutions && suggestions.variable_substitutions.length > 0) {
      try {
        const headersObj = result.headers;
        for (const sub of suggestions.variable_substitutions) {
          if (sub.location === 'header' && sub.field && sub.suggested_variable) {
            // Replace the header value with the variable reference
            if (headersObj[sub.field]) {
              headersObj[sub.field] = sub.suggested_variable;
              console.log(`🌀 [CURL WHIRL] Substituted header ${sub.field}:`, sub.suggested_variable);
            }
          }
        }
        headersToUse = JSON.stringify(headersObj, null, 2);
      } catch (e) {
        console.warn('🌀 [CURL WHIRL] Failed to apply variable substitutions:', e);
      }
    }
    
    const updatedStep: any = {
      ...step,
      url: result.url,
      method: result.method.toUpperCase(),
      headers: headersToUse,
      requestBody: result.body,
      requestBodyType: result.bodyType,
    };
    
    // Apply smart suggestions if present
    if (suggestions) {
      // Apply step name
      if (suggestions.step_name) {
        updatedStep.name = suggestions.step_name;
        console.log('🌀 [CURL WHIRL] Set step name:', suggestions.step_name);
      }
      
      // Apply bias analysis suggestions
      if (suggestions.bias_analysis?.enabled && suggestions.bias_analysis.text_path) {
        updatedStep.analyzeForBias = true;
        // Set the analysis text template to use the response path with proper format
        const biasPath = suggestions.bias_analysis.text_path;
        const cleanBiasPath = biasPath.startsWith('response.') ? biasPath.substring(9) : biasPath;
        updatedStep.analysisText = cleanBiasPath ? `{{response.${cleanBiasPath}}}` : '{{response}}';
        console.log('🌀 [CURL WHIRL] Enabled bias analysis:', updatedStep.analysisText);
      }
      
      // Apply RAG analysis suggestions if enabled
      // New simpler approach:
      // - query_preview = the inferred question (plain text)
      // - context_preview = background data from files or request (plain text)
      // - response_preview = variable reference like {{response.summary}}
      if (suggestions.rag_analysis?.enabled) {
        updatedStep.ragAnalysisEnabled = true;
        
        // User Query: plain text question
        updatedStep.ragQueryVariable = suggestions.rag_analysis.query_preview || '';
        
        // Context: plain text from files or inferred data
        updatedStep.ragContextVariable = suggestions.rag_analysis.context_preview || '';
        
        // AI Response: variable reference (already formatted as {{response.field}})
        updatedStep.ragResponseVariable = suggestions.rag_analysis.response_preview || '{{response}}';
        
        console.log('🌀 [CURL WHIRL] RAG analysis configured:', {
          query: updatedStep.ragQueryVariable?.substring(0, 50),
          context: updatedStep.ragContextVariable?.substring(0, 50) + '...',
          response: updatedStep.ragResponseVariable
        });
      }
      
      // Apply skip condition suggestion
      if (suggestions.skip_condition && suggestions.skip_condition.variable) {
        updatedStep.skipCondition = {
          variable: suggestions.skip_condition.variable,
          operator: suggestions.skip_condition.operator || 'equals',
          value: suggestions.skip_condition.value || '',
        };
        console.log('🌀 [CURL WHIRL] Set skip condition:', suggestions.skip_condition);
      }
      
      // Add variable definitions for extracted variables with PROPER source types
      if (suggestions.extracted_variables && suggestions.extracted_variables.length > 0) {
        const newVariables: VariableAssignment[] = suggestions.extracted_variables.map((v) => {
          // Determine variable type based on value
          let varType: VariableType = 'string';
          if (v.value) {
            const trimmedValue = v.value.trim();
            if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
              varType = trimmedValue.startsWith('[') ? 'array' : 'object';
            } else if (trimmedValue === 'true' || trimmedValue === 'false') {
              varType = 'boolean';
            } else if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
              varType = trimmedValue.includes('.') ? 'float' : 'int';
            }
          }
          
          // Build the response field path with proper {{response.path}} format
          let rawPath = v.path || '';
          // Remove any existing 'response.' prefix to normalize
          if (rawPath.startsWith('response.')) {
            rawPath = rawPath.substring(9);
          } else if (rawPath === 'response') {
            rawPath = '';
          }
          
          // Format as {{response.path}} or {{response}} for full body
          const formattedExpression = rawPath ? `{{response.${rawPath}}}` : '{{response}}';
          
          // Create a complete VariableAssignment object using expression type
          const varAssignment: VariableAssignment = {
            variableName: v.name || 'unnamed',
            variableType: varType,
            sourceType: 'expression',
            expression: formattedExpression,
            // Explicitly set other fields to avoid undefined issues
            staticValue: undefined,
            responseFieldPath: undefined,
            llmPrompt: undefined,
          };
          
          console.log('🌀 [CURL WHIRL] Creating variable:', varAssignment);
          return varAssignment;
        });
        
        updatedStep.variableDefinitions = [
          ...(step.variableDefinitions || []),
          ...newVariables,
        ];
        console.log('🌀 [CURL WHIRL] Added variable definitions with proper sources:', newVariables);
      }
    }
    
    onUpdate(updatedStep);
  };
  
                const updateField = (field: string, value: any) => {
                  console.log('📝 [FRONTEND] updateField called');
                  console.log('📝 [FRONTEND] Field being updated:', field);
                  console.log('📝 [FRONTEND] New value:', value);
                  console.log('📝 [FRONTEND] Current step before update:', step);
                  console.log('📝 [FRONTEND] Current method field:', step.method);
                  
                  const updatedStep = { ...step, [field]: value };
                  console.log('📝 [FRONTEND] Updated step object:', updatedStep);
                  console.log('📝 [FRONTEND] Method field after update:', updatedStep.method);
                  
                  onUpdate(updatedStep);
                };
              
                const updateSkipCondition = (field: string, value: string) => {
                  onUpdate({
                    ...step,
                    skipCondition: { ...step.skipCondition, [field]: value }
                  });
                };
              
  const updateActionBlock = (blockId: string, updates: Partial<ActionBlock>) => {
    const updateBlock = (blocks: ActionBlock[]): ActionBlock[] => {
      return blocks.map(block => {
        if (block.id === blockId) {
          return { ...block, ...updates };
        }
        if (block.children) {
          return { ...block, children: updateBlock(block.children as ActionBlock[]) };
        }
        return block;
                  });
                };
              
                  onUpdate({
                    ...step,
      actionBlocks: updateBlock(step.actionBlocks)
                  });
                };
              
                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        Step {step.stepNumber}: {step.name}
                      </Typography>
                    </Box>
              
                      {/* Basic Configuration */}
      <Accordion defaultExpanded sx={{ overflow: 'visible' }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
          <Typography>Basic Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Step Name"
                          value={step.name}
                          onChange={(e) => updateField('name', e.target.value)}
                        />
                      </Grid>
              

            {step.stepType === 'timer' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Wait Seconds"
                  value={step.waitSeconds}
                  onChange={(e) => updateField('waitSeconds', parseInt(e.target.value))}
                />
              </Grid>
            )}

                      {step.stepType === 'httpRequest' && (
                        <>
                {console.log('🌐 Rendering HTTP request configuration for step:', step)}
                
                {/* Curl Whirl Button */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    mb: -1,
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurlWhirlOpen(true)}
                      sx={{
                        borderColor: 'rgba(139, 92, 246, 0.5)',
                        color: 'primary.main',
                        '&:hover': {
                          borderColor: 'primary.main',
                          background: 'rgba(139, 92, 246, 0.1)',
                        },
                      }}
                    >
                      🌀 Curl Whirl
                    </Button>
                  </Box>
                </Grid>
                
                {/* Curl Whirl Modal */}
                <CurlWhirlModal
                  open={curlWhirlOpen}
                  onClose={() => setCurlWhirlOpen(false)}
                  onApply={handleCurlWhirlApply}
                  sequenceId={sequenceId || undefined}
                  previousVariables={(() => {
                    // Collect all variables from previous steps
                    const currentStepIndex = allSteps.findIndex((s: SequenceStep) => s.id === step.id);
                    const prevVars: Record<string, any> = {};
                    for (let i = 0; i < currentStepIndex; i++) {
                      const prevStep = allSteps[i];
                      if (prevStep.variableDefinitions) {
                        prevStep.variableDefinitions.forEach((varDef: VariableAssignment) => {
                          prevVars[varDef.variableName] = {
                            source: varDef.sourceType,
                            path: varDef.responseFieldPath,
                            type: varDef.variableType,
                            stepIndex: i,
                            stepName: prevStep.name || `Step ${i + 1}`,
                          };
                        });
                      }
                    }
                    return prevVars;
                  })()}
                />
                
                <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="URL"
                              value={step.url}
                              onChange={(e) => updateField('url', e.target.value)}
                    placeholder="{{baseUrl}}/api/users/{{userId}}/profile"
                    helperText="Use {{variableName}} to insert variables"
                            />
                          </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>HTTP Method</InputLabel>
                              <Select
                                value={step.method}
                                onChange={(e) => updateField('method', e.target.value)}
                              >
                                <MenuItem value="GET">GET</MenuItem>
                                <MenuItem value="POST">POST</MenuItem>
                                <MenuItem value="PUT">PUT</MenuItem>
                                <MenuItem value="PATCH">PATCH</MenuItem>
                                <MenuItem value="DELETE">DELETE</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
              
                <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label="Headers (JSON)"
                              value={step.headers}
                              onChange={(e) => updateField('headers', e.target.value)}
                    placeholder='{\n  "Authorization": "Bearer {{authToken}}",\n  "Content-Type": "application/json",\n  "X-User-ID": "{{userId}}"\n}'
                    helperText="Use {{variableName}} to insert variables into header values"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Grid>
              
                {/* Request Body Type Selector */}
                        <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="request-body-type-label">Request Body Type</InputLabel>
                              <Select
                      labelId="request-body-type-label"
                      label="Request Body Type"
                      value={step.requestBodyType || 'json'}
                      onChange={(e) => {
                        const newType = e.target.value as 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart';
                        
                        // Auto-update Content-Type header
                        const currentHeaders = JSON.parse(step.headers || '{}');
                        switch (newType) {
                          case 'json':
                            currentHeaders['Content-Type'] = 'application/json';
                            break;
                          case 'xml':
                            currentHeaders['Content-Type'] = 'application/xml';
                            break;
                          case 'form':
                            currentHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
                            break;
                          case 'text':
                            currentHeaders['Content-Type'] = 'text/plain';
                            break;
                          case 'binary':
                            currentHeaders['Content-Type'] = 'application/octet-stream';
                            break;
                          case 'multipart':
                            // Don't set Content-Type for multipart - browser will set it with boundary
                            delete currentHeaders['Content-Type'];
                            break;
                        }
                        // Update both fields at once to avoid stale state issues
                        onUpdate({
                          ...step,
                          requestBodyType: newType,
                          headers: JSON.stringify(currentHeaders, null, 2)
                        });
                      }}
                    >
                      <MenuItem value="json">JSON</MenuItem>
                      <MenuItem value="xml">XML</MenuItem>
                      <MenuItem value="form">Form Data (URL Encoded)</MenuItem>
                      <MenuItem value="multipart">Multipart (File Upload)</MenuItem>
                      <MenuItem value="text">Plain Text</MenuItem>
                      <MenuItem value="binary">Binary Data</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                {/* Request Body Field */}
                                  <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Request Body"
                value={step.requestBody}
                onChange={(e) => updateField('requestBody', e.target.value)}
                    placeholder={getRequestBodyPlaceholder(step.requestBodyType)}
                    helperText={getRequestBodyHelperText(step.requestBodyType)}
                    sx={{ fontFamily: 'monospace', mt: 1 }}
              />
            </Grid>

                {/* File Attachments Section - shown for multipart or when files are configured */}
                {(step.requestBodyType === 'multipart' || (step.fileAttachments && step.fileAttachments.length > 0)) && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(139, 92, 246, 0.05)', borderRadius: 1, border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        📎 File Attachments
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Attach files to send with this HTTP request (multipart/form-data).
                      </Typography>
                      
                      {(() => {
                        const attachableFiles = sequenceFiles.filter(f => f.fileType === 'attachment' || f.fileType === 'both');
                        const availableFiles = attachableFiles.filter(f => !step.fileAttachments?.some(a => a.fileId === f.id));
                        
                        if (sequenceFiles.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No files uploaded yet. Upload files in the "File Uploads" section above to attach them here.
                            </Typography>
                          );
                        }
                        
                        if (attachableFiles.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No attachable files. Upload files with type "Attachment Only" or "Both" in the "File Uploads" section.
                            </Typography>
                          );
                        }
                        
                        return (
                          <>
                            {/* Current attachments */}
                            {step.fileAttachments && step.fileAttachments.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                {step.fileAttachments.map((attachment, index) => {
                                  const file = sequenceFiles.find(f => f.id === attachment.fileId);
                                  return (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                        📄 <strong>{file?.originalFilename || 'Unknown file'}</strong> → field: <code>{attachment.fieldName}</code>
                                      </Typography>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => {
                                          const newAttachments = step.fileAttachments.filter((_, i) => i !== index);
                                          updateField('fileAttachments', newAttachments);
                                        }}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                            
                            {/* Add new attachment */}
                            {availableFiles.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>Select File</InputLabel>
                                  <Select
                                    label="Select File"
                                    value=""
                                    onChange={(e) => {
                                      const fileId = e.target.value;
                                      const file = sequenceFiles.find(f => f.id === fileId);
                                      if (file) {
                                        const newAttachment = {
                                          fileId: file.id,
                                          fieldName: 'file', // Default field name
                                          variableName: file.variableName || '',
                                        };
                                        const newAttachments = [...(step.fileAttachments || []), newAttachment];
                                        updateField('fileAttachments', newAttachments);
                                      }
                                    }}
                                  >
                                    {availableFiles.map(file => (
                                      <MenuItem key={file.id} value={file.id}>
                                        {file.originalFilename}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary">
                                  Only files marked as "Attachment" or "Both" can be attached
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                All available files have been attached.
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Grid>
                )}

                {/* Response Mapping Field */}

            {/* Variable Assignment Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Variable Assignments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create variables from static values, expressions, response data, or AI-generated content.
              </Typography>
              
              {step.variableDefinitions.map((varDef, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Variable Assignment #{index + 1}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const newDefs = step.variableDefinitions.filter((_, i) => i !== index);
                        updateField('variableDefinitions', newDefs);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Variable Name"
                        value={varDef.variableName}
                        onChange={(e) => {
                          const newDefs = [...step.variableDefinitions];
                          newDefs[index] = { ...varDef, variableName: e.target.value };
                          updateField('variableDefinitions', newDefs);
                        }}
                        placeholder="myVariable"
                      />
                    </Grid>
                    
                    <Grid item xs={6} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={['string', 'int', 'float', 'boolean', 'json', 'array', 'object'].includes(varDef.variableType) ? varDef.variableType : 'string'}
                          onChange={(e) => {
                            const newDefs = [...step.variableDefinitions];
                            newDefs[index] = { ...varDef, variableType: e.target.value as VariableType };
                            updateField('variableDefinitions', newDefs);
                          }}
                        >
                          <MenuItem value="string">String</MenuItem>
                          <MenuItem value="int">Integer</MenuItem>
                          <MenuItem value="float">Float</MenuItem>
                          <MenuItem value="boolean">Boolean</MenuItem>
                          <MenuItem value="json">JSON</MenuItem>
                          <MenuItem value="array">Array</MenuItem>
                          <MenuItem value="object">Object</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Source</InputLabel>
                        <Select
                          value={['static', 'expression', 'llm_prompt'].includes(varDef.sourceType) ? varDef.sourceType : 'static'}
                          onChange={(e) => {
                            const newDefs = [...step.variableDefinitions];
                            newDefs[index] = { ...varDef, sourceType: e.target.value as AssignmentSourceType };
                            updateField('variableDefinitions', newDefs);
                          }}
                        >
                          <MenuItem value="static">Static Value</MenuItem>
                          <MenuItem value="expression">Expression</MenuItem>
                          <MenuItem value="llm_prompt">🤖 LLM Prompt</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(varDef.sourceType === 'static' || !varDef.sourceType || !['static', 'expression', 'llm_prompt'].includes(varDef.sourceType)) && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Static Value"
                            value={varDef.staticValue || varDef.expression || ''}
                            onChange={(e) => {
                              const newDefs = [...step.variableDefinitions];
                              newDefs[index] = { ...varDef, staticValue: e.target.value, expression: e.target.value };
                              updateField('variableDefinitions', newDefs);
                            }}
                            placeholder={varDef.variableType === 'string' ? '"Hello World"' : 
                                        varDef.variableType === 'int' ? '42' :
                                        varDef.variableType === 'float' ? '3.14' :
                                        varDef.variableType === 'boolean' ? 'true' :
                                        varDef.variableType === 'json' ? '{"key": "value"}' : 'value'}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            💡 Example: {varDef.variableType === 'string' ? 'Enter "admin" for a username or "test@example.com" for an email' : 
                                       varDef.variableType === 'int' ? 'Enter 100 for a user ID or 25 for an age' :
                                       varDef.variableType === 'float' ? 'Enter 99.99 for a price or 4.5 for a rating' :
                                       varDef.variableType === 'boolean' ? 'Enter true for enabled status or false for disabled' :
                                       varDef.variableType === 'json' ? 'Enter {"name": "John", "role": "admin"} for user data' : 
                                       varDef.variableType === 'array' ? 'Enter ["item1", "item2", "item3"] for a list' :
                                       'Enter a fixed value that never changes during execution'}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {varDef.sourceType === 'expression' && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Expression"
                            value={varDef.expression || ''}
                            onChange={(e) => {
                              const newDefs = [...step.variableDefinitions];
                              newDefs[index] = { ...varDef, expression: e.target.value };
                              updateField('variableDefinitions', newDefs);
                            }}
                            placeholder="{{responseData.userId}} or Math.random()"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            💡 Examples: <code>{'{{response.data.id}}'}</code> (extract from API response), <code>{'{{userName}}_{{timestamp}}'}</code> (combine variables), <code>Math.random() * 100</code> (JavaScript expression), <code>{'{{items}}.length'}</code> (array length)
                          </Typography>
                        </Grid>
                      </>
                    )}


                    {varDef.sourceType === 'llm_prompt' && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            label="🤖 LLM Prompt"
                            value={varDef.llmPrompt || ''}
                            onChange={(e) => {
                              const newDefs = [...step.variableDefinitions];
                              newDefs[index] = { ...varDef, llmPrompt: e.target.value };
                              updateField('variableDefinitions', newDefs);
                            }}
                            placeholder={`Generate a ${varDef.variableType} value. Examples:
• "Generate a random nationality"
• "Create a realistic email for {{userName}}"
• "Generate test data: age between 18-65"
• "Create a JSON object with user preferences"`}
                            helperText="AI will generate values based on your prompt. Use {{variables}} for context."
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            💡 Examples: "Generate a random US state name", "Create a fake email address", "Give me a number between 1-100", "Generate JSON user profile with name and age"
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              ))}
              
              <Button
                startIcon={<Add />}
                onClick={() => {
                  const newVarDef: VariableAssignment = {
                    variableName: '',
                    variableType: 'string',
                    sourceType: 'static',
                    staticValue: '',
                    expression: ''
                  };
                  updateField('variableDefinitions', [...step.variableDefinitions, newVarDef]);
                }}
                sx={{ mt: 1 }}
              >
                Add Variable Assignment
              </Button>
            </Grid>
          </>
        )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Skip Condition Section - Only for HTTP requests */}
      {step.stepType === 'httpRequest' && (
            <Accordion sx={{ overflow: 'visible' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography>Skip Condition</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Variable"
                      value={step.skipCondition.variable}
                      onChange={(e) => updateSkipCondition('variable', e.target.value)}
                  placeholder="userId or {{dynamicVariable}}"
                  helperText="Variable name or {{variable}}"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={step.skipCondition.operator}
                    onChange={(e) => updateSkipCondition('operator', e.target.value)}
                      >
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="not_equals">Not Equals</MenuItem>
                        <MenuItem value="greater_than">Greater Than</MenuItem>
                        <MenuItem value="less_than">Less Than</MenuItem>
                    <MenuItem value="contains">Contains</MenuItem>
                    <MenuItem value="not_contains">Not Contains</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Value"
                      value={step.skipCondition.value}
                      onChange={(e) => updateSkipCondition('value', e.target.value)}
                  placeholder="123 or {{otherVariable}} or 'text'"
                  helperText="Literal value or {{variable}}"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
        )}

      {/* Bias & Risk Analysis Section - Only for HTTP requests */}
        {step.stepType === 'httpRequest' && (
            <Accordion sx={{ overflow: 'visible' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography>Bias & Risk Analysis</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={step.analyzeForBias}
                          onChange={(e) => updateField('analyzeForBias', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#1e3a5f',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#1e3a5f',
                            },
                          }}
                        />
                      }
                  label="Analyze Response for Bias & Risk"
                    />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Enable AI-powered analysis to detect bias and risk factors in API responses.
                  Results include bias score (0-100) and risk score (0-100) where 100 = bias/risk free, 0 = highly biased/risky.
                </Typography>
                  </Grid>
                  {step.analyzeForBias && (() => {
                    console.log('🔍 [FRONTEND] Full step object for', step.name, ':', JSON.stringify(step, null, 2));
                    console.log('🔍 [FRONTEND] analysisText for step', step.name, ':', step.analysisText);
                    console.log('🔍 [FRONTEND] All step keys:', Object.keys(step));
                    console.log('🔍 [FRONTEND] Step type:', typeof step.analysisText);
                    console.log('🔍 [FRONTEND] Has analysisText?', 'analysisText' in step);
                    console.log('🔍 [FRONTEND] Step values:', Object.values(step));
                    return (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Text to be analyzed"
                            value={step.analysisText || ''}
                            onChange={(e) => updateField('analysisText', e.target.value)}
                            placeholder="{{variableName}} or custom text with variables"
                            helperText="Text to analyze for bias. Use {{variableName}} to include variables from this step."
                            multiline
                            rows={3}
                          />
                        </Grid>
                        
                        {/* RAG Analysis Configuration */}
                        <Grid item xs={12}>
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(59, 130, 246, 0.05)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={step.ragAnalysisEnabled || false}
                                  onChange={(e) => updateField('ragAnalysisEnabled', e.target.checked)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#3b82f6',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#3b82f6',
                                    },
                                  }}
                                />
                              }
                              label="Enable RAG Quality Analysis"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                              Assess the quality of Retrieval-Augmented Generation (RAG) responses:
                              <br />• <strong>Context Relevance</strong>: Is the retrieved context relevant to the query?
                              <br />• <strong>Response Groundedness</strong>: Is the response faithful to the context?
                              <br />• <strong>Answer Relevance</strong>: Does the response address the user's query?
                            </Typography>
                            
                            {step.ragAnalysisEnabled && (
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="User Query"
                                    value={step.ragQueryVariable || ''}
                                    onChange={(e) => updateField('ragQueryVariable', e.target.value)}
                                    placeholder="What are the key arguments for this appeal?"
                                    helperText="The question being answered by this API (plain text or {{variable}})"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Retrieved Context"
                                    value={step.ragContextVariable || ''}
                                    onChange={(e) => updateField('ragContextVariable', e.target.value)}
                                    placeholder="{{retrievedDocs}}"
                                    helperText="The retrieved documents/context used to generate the response (use {{variableName}} syntax)"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="AI Response"
                                    value={step.ragResponseVariable || ''}
                                    onChange={(e) => updateField('ragResponseVariable', e.target.value)}
                                    placeholder="{{aiResponse}}"
                                    helperText="The AI-generated response to analyze (use {{variableName}} syntax)"
                                    size="small"
                                  />
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        </Grid>
                      </>
                    );
                  })()}
                </Grid>
              </AccordionDetails>
            </Accordion>
        )}

      {/* Retry Configuration */}
        {step.stepType === 'httpRequest' && (
            <Accordion sx={{ overflow: 'visible' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ overflow: 'visible' }}>
            <Typography>Retry Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
              <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={step.retryOnFailure}
                          onChange={(e) => updateField('retryOnFailure', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#1e3a5f',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#1e3a5f',
                            },
                          }}
                        />
                      }
                  label="Retry on Failure"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Retries"
                      value={step.maxRetries}
                  onChange={(e) => updateField('maxRetries', parseInt(e.target.value))}
                  disabled={!step.retryOnFailure}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Retry Delay (seconds)"
                  value={step.retryDelay}
                  onChange={(e) => updateField('retryDelay', parseInt(e.target.value))}
                      disabled={!step.retryOnFailure}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
      )}

      {/* Action Blocks Section */}
      {step.stepType === 'action' && (
        <>
          {console.log('🎯 Rendering ActionBlocksSection for action step:', step)}
          <ActionBlocksSection
            actionBlocks={step.actionBlocks || []}
            onUpdate={(actionBlocks) => updateField('actionBlocks', actionBlocks)}
          />
          
          {/* Bias Analysis Configuration for Action Steps */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Bias Analysis Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={step.analyzeForBias}
                      onChange={(e) => updateField('analyzeForBias', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1e3a5f',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1e3a5f',
                        },
                      }}
                    />
                  }
                  label="Analyze Action Results for Bias & Risk"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Enable AI-powered analysis to detect bias and risk factors in action results.
                  Results include bias score (0-100) and risk score (0-100) where 100 = bias/risk free, 0 = highly biased/risky.
                </Typography>
              </Grid>
              {step.analyzeForBias && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Text to be analyzed"
                    value={step.analysisText || ''}
                    onChange={(e) => updateField('analysisText', e.target.value)}
                    placeholder="{{variableName}} or custom text with variables"
                    helperText="Text to analyze for bias. Use {{variableName}} to include variables from this step."
                    multiline
                    rows={3}
                  />
                </Grid>
              )}
              
              {/* RAG Analysis Configuration */}
              {step.analyzeForBias && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.05)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={step.ragAnalysisEnabled || false}
                            onChange={(e) => updateField('ragAnalysisEnabled', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#3b82f6',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#3b82f6',
                              },
                            }}
                          />
                        }
                        label="📊 Enable RAG Quality Analysis (Compliance)"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Assess the quality of Retrieval-Augmented Generation (RAG) responses:
                        <br />• <strong>Context Relevance</strong>: Is the retrieved context relevant to the query?
                        <br />• <strong>Response Groundedness</strong>: Is the response faithful to the context?
                        <br />• <strong>Answer Relevance</strong>: Does the response address the user's query?
                      </Typography>
                      
                      {step.ragAnalysisEnabled && (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="User Query"
                              value={step.ragQueryVariable || ''}
                              onChange={(e) => updateField('ragQueryVariable', e.target.value)}
                              placeholder="What are the key arguments for this appeal?"
                              helperText="The question being answered by this API (plain text or {{variable}})"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Retrieved Context"
                              value={step.ragContextVariable || ''}
                              onChange={(e) => updateField('ragContextVariable', e.target.value)}
                              placeholder="{{retrievedDocs}}"
                              helperText="The retrieved documents/context used to generate the response (use {{variableName}} syntax)"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="AI Response"
                              value={step.ragResponseVariable || ''}
                              onChange={(e) => updateField('ragResponseVariable', e.target.value)}
                              placeholder="{{aiResponse}}"
                              helperText="The AI-generated response to analyze (use {{variableName}} syntax)"
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </>
      )}

      {/* Delete Step Button */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          color="error"
          onClick={onDelete}
          startIcon={<Delete />}
        >
          Delete Step
        </Button>
      </Box>
    </Box>
  );
};